import React, { useState, useEffect, useMemo } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Table from '../../components/common/Table/Table';
import Modal from '../../components/common/Modal/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import { getAllMentors, createMentor, deleteMentor, suspendMentor, unsuspendMentor, updateMentorRate, updateMentorInvoiceFlag } from '../../services/mentorService';
import useForm from '../../hooks/useForm';
import useAuth from '../../hooks/useAuth';
import { useToast, useConfirm } from '../../context/ToastContext';
import styles from './Mentors.module.css';

const Mentors = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCredentials, setShowCredentials] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [rateEditMentor, setRateEditMentor] = useState(null);
  const [rateValue, setRateValue] = useState('');
  const [savingRate, setSavingRate] = useState(false);
  const [togglingInvoiceFlagId, setTogglingInvoiceFlagId] = useState(null);
  const toast = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    fetchMentors();
  }, []);

  const fetchMentors = async () => {
    try {
      const data = await getAllMentors();
      setMentors(data);
    } catch (error) {
      console.error('Error fetching mentors:', error);
    } finally {
      setLoading(false);
    }
  };

  const { values, handleChange, handleSubmit, resetForm , isSubmitting} = useForm(
    {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      linkedin_profile: '',
      description: '',
      skills: [],
      timezone: '',
      max_students: 5,
      track: [],
      preferred_project_types: [],
      years_of_experience: 0,
      highest_qualification: '',
      institution_organization: ''
    },
    async (formValues) => {
      try {
        const result = await createMentor(formValues);
        setShowCredentials(result.credentials);
        setShowModal(false);
        resetForm();
        fetchMentors();
      } catch (error) {
        toast.error('Error creating mentor: ' + error.message);
      }
    }
  );

  const handleDelete = async (mentorId) => {
    const confirmed = await confirm('Are you sure you want to delete this mentor?', { confirmText: 'Delete', variant: 'danger' });
    if (confirmed) {
      try {
        await deleteMentor(mentorId);
        fetchMentors();
      } catch (error) {
        toast.error('Error deleting mentor: ' + error.message);
      }
    }
  };

  const handleSuspend = async (userId, isSuspended) => {
    const action = isSuspended ? 'unsuspend' : 'suspend';
    const confirmMessage = isSuspended 
      ? 'Are you sure you want to unsuspend this mentor? They will be able to login again.'
      : 'Are you sure you want to suspend this mentor? They will not be able to login.';
    
    const confirmed = await confirm(confirmMessage, { confirmText: isSuspended ? 'Unsuspend' : 'Suspend', variant: 'warning' });
    if (confirmed) {
      try {
        if (isSuspended) {
          await unsuspendMentor(userId);
        } else {
          await suspendMentor(userId, 'Suspended by administrator');
        }
        fetchMentors();
        toast.success(`Mentor ${action}ed successfully`);
      } catch (error) {
        toast.error(`Error ${action}ing mentor: ${error.message}`);
      }
    }
  };

  const openRateEditor = (mentor) => {
    setRateEditMentor(mentor);
    setRateValue(mentor.per_hour_rate ?? '');
  };

  const handleSaveRate = async () => {
    const rate = Number(rateValue);
    if (!Number.isFinite(rate) || rate < 0) {
      toast.error('Enter a valid non-negative rate');
      return;
    }
    setSavingRate(true);
    try {
      const updated = await updateMentorRate(rateEditMentor.id, rate);
      toast.success(`Rate saved: ₹${updated.per_hour_rate}/hr for ${rateEditMentor.first_name} ${rateEditMentor.last_name}`);
      setRateEditMentor(null);
      fetchMentors();
    } catch (error) {
      toast.error('Error updating rate: ' + error.message);
    } finally {
      setSavingRate(false);
    }
  };

  const handleToggleInvoiceFlag = async (mentor) => {
    setTogglingInvoiceFlagId(mentor.id);
    try {
      const updated = await updateMentorInvoiceFlag(mentor.id, !mentor.invoice_generate);
      toast.success(
        `Invoice generation ${updated.invoice_generate ? 'enabled' : 'disabled'} for ${mentor.first_name} ${mentor.last_name}`
      );
      fetchMentors();
    } catch (error) {
      toast.error('Error updating invoice generation flag: ' + error.message);
    } finally {
      setTogglingInvoiceFlagId(null);
    }
  };

  // Filter mentors based on search query
  const filteredMentors = useMemo(() => {
    if (!searchQuery) return mentors;
    
    const query = searchQuery.toLowerCase();
    return mentors.filter(mentor => {
      const fullName = `${mentor.first_name} ${mentor.last_name}`.toLowerCase();
      const email = mentor.email?.toLowerCase() || '';
      return fullName.includes(query) || email.includes(query);
    });
  }, [mentors, searchQuery]);

  const columns = [
    { 
      header: 'Name', 
      accessor: 'first_name',
      sortKey: 'first_name',
      sortValue: (row) => `${row.first_name} ${row.last_name}`,
      render: (row) => (
        <div>
          {`${row.first_name} ${row.last_name}`}
          {row.is_suspended && <span style={{ marginLeft: '8px', padding: '2px 8px', fontSize: '11px', background: '#ff9800', color: 'white', borderRadius: '4px', fontWeight: 'bold' }}>SUSPENDED</span>}
        </div>
      )
    },
    { header: 'Email', accessor: 'email' },
    { header: 'Phone', accessor: 'phone', sortable: false },
    { header: 'Students Assigned', accessor: 'number_of_students_assigned' },
    { header: 'Max Students', accessor: 'max_students', sortable: false },
    ...(isSuperAdmin ? [{
      header: 'Rate (₹/hr)',
      sortable: false,
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{row.per_hour_rate ?? '—'}</span>
          <Button size="small" variant="outline" onClick={() => openRateEditor(row)}>
            {row.per_hour_rate ? 'Edit' : 'Set Rate'}
          </Button>
        </div>
      )
    }] : []),
    ...(isSuperAdmin ? [{
      header: 'Invoice Generation',
      sortable: false,
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: row.invoice_generate ? '#2e7d32' : '#757575', fontWeight: 'bold' }}>
            {row.invoice_generate ? 'Enabled' : 'Disabled'}
          </span>
          <Button
            size="small"
            variant="outline"
            onClick={() => handleToggleInvoiceFlag(row)}
            loading={togglingInvoiceFlagId === row.id}
          >
            {row.invoice_generate ? 'Disable' : 'Enable'}
          </Button>
        </div>
      )
    }] : []),
    ...(isSuperAdmin ? [{
      header: 'Actions',
      sortable: false,
      render: (row) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button size="small" 
            variant={row.is_suspended ? 'primary' : 'warning'} 
            onClick={() => handleSuspend(row.user_id, row.is_suspended)} loading={loading}
            style={{ 
              backgroundColor: row.is_suspended ? '#4CAF50' : '#FF9800',
              minWidth: '90px'
            }}
          >
            {row.is_suspended ? 'Unsuspend' : 'Suspend'}
          </Button>
          <Button size="small" variant="danger" onClick={() => handleDelete(row.id)} loading={loading}>
            Delete
          </Button>
        </div>
      )
    }] : [])
  ];

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  return (
    <div className={styles.mentorsPage}>
      <div className={styles.header}>
        <h1>Mentors</h1>
        <div className={styles.headerActions}>
          <SearchBar 
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search mentors by name or email..."
          />
          <Button icon="+" onClick={() => setShowModal(true)} loading={loading}>Add Mentor</Button>
        </div>
      </div>

      <Card>
        <Table columns={columns} data={filteredMentors} />
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add New Mentor" size="large">
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>First Name *</label>
              <input name="first_name" value={values.first_name} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Last Name *</label>
              <input name="last_name" value={values.last_name} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Email *</label>
              <input type="email" name="email" value={values.email} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Phone *</label>
              <input name="phone" value={values.phone} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>LinkedIn Profile</label>
              <input name="linkedin_profile" value={values.linkedin_profile} onChange={handleChange} />
            </div>
            <div className={styles.formGroup}>
              <label>Timezone</label>
              <input name="timezone" value={values.timezone} onChange={handleChange} />
            </div>
            <div className={styles.formGroup}>
              <label>Max Students</label>
              <input type="number" name="max_students" value={values.max_students} onChange={handleChange} />
            </div>
            <div className={styles.formGroup}>
              <label>Years of Experience</label>
              <input type="number" name="years_of_experience" value={values.years_of_experience} onChange={handleChange} />
            </div>
            <div className={styles.formGroup}>
              <label>Highest Qualification</label>
              <input name="highest_qualification" value={values.highest_qualification} onChange={handleChange} />
            </div>
            <div className={styles.formGroup}>
              <label>Institution/Organization</label>
              <input name="institution_organization" value={values.institution_organization} onChange={handleChange} />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Description</label>
            <textarea name="description" value={values.description} onChange={handleChange} rows="3" />
          </div>
          <div className={styles.formActions}>
            <Button type="button" variant="outline" onClick={() => setShowModal(false)} loading={loading}>Cancel</Button>
            <Button type="submit" loading={isSubmitting}>Create Mentor</Button>
          </div>
        </form>
      </Modal>

      {rateEditMentor && (
        <Modal isOpen={!!rateEditMentor} onClose={() => setRateEditMentor(null)} title={`Set Hourly Rate — ${rateEditMentor.first_name} ${rateEditMentor.last_name}`}>
          <div className={styles.formGroup}>
            <label>Rate (₹ per hour)</label>
            <input
              type="number"
              min="0"
              value={rateValue}
              onChange={(e) => setRateValue(e.target.value)}
              autoFocus
            />
          </div>
          <div className={styles.formActions}>
            <Button type="button" variant="outline" onClick={() => setRateEditMentor(null)} loading={savingRate}>Cancel</Button>
            <Button type="button" onClick={handleSaveRate} loading={savingRate}>Save</Button>
          </div>
        </Modal>
      )}

      {showCredentials && (
        <Modal isOpen={!!showCredentials} onClose={() => setShowCredentials(null)} title="Mentor Credentials">
          <div className={styles.credentials}>
            <p><strong>Username:</strong> {showCredentials.username}</p>
            <p><strong>Email:</strong> {showCredentials.email}</p>
            <p><strong>Password:</strong> {showCredentials.password}</p>
            <p className={styles.warning}>Alert: Please save these credentials securely. They will not be shown again.</p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Mentors;